import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isDoaga } from "../lib/chf";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AuthImg, UploadButton } from "../components/AuthImg";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Unlock, Download } from "lucide-react";
import { exportCSV } from "../lib/csvExport";
import { useConfirm } from "../components/ConfirmProvider";
import ErrorBoundary from "../components/ErrorBoundary";

const ACTIONS = ["view", "add", "edit", "delete"];
const ACTION_LABELS = { view: "Vizualizare", add: "Adăugare", edit: "Modificare", delete: "Ștergere" };

const PASSWORD_RULES = [
  { code: "min_len", label: "Minim 8 caractere", test: (p) => p.length >= 8 },
  { code: "uppercase", label: "Cel puțin o literă mare (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { code: "lowercase", label: "Cel puțin o literă mică (a-z)", test: (p) => /[a-z]/.test(p) },
  { code: "digit", label: "Cel puțin o cifră (0-9)", test: (p) => /\d/.test(p) },
];

function PasswordStrength({ value }) {
  if (!value) return null;
  const passed = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(value) }));
  const score = passed.filter((r) => r.ok).length;
  const bonusLong = value.length >= 12;
  const bonusSpecial = /[^A-Za-z0-9]/.test(value);
  const total = score + (bonusLong ? 0.5 : 0) + (bonusSpecial ? 0.5 : 0);
  let label = "Slabă", color = "var(--red-bila)", pct = 25;
  if (total >= 5) { label = "Foarte puternică"; color = "var(--green-valid)"; pct = 100; }
  else if (total >= 4) { label = "Puternică"; color = "var(--green-valid)"; pct = 80; }
  else if (total >= 3) { label = "Medie"; color = "var(--orange-bila)"; pct = 60; }
  else if (total >= 2) { label = "Acceptabilă"; color = "var(--orange-bila)"; pct = 40; }
  return (
    <div className="mt-2 space-y-1.5" data-testid="password-strength">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500">Putere parolă</span>
        <span style={{ color }} className="font-semibold" data-testid="password-strength-label">{label}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
      <ul className="text-[11px] space-y-0.5 mt-1">
        {passed.map((r) => (
          <li key={r.code} className={r.ok ? "text-[var(--green-valid)]" : "text-zinc-400"}>
            {r.ok ? "✓" : "○"} {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Settings() {
  const { company, refreshCompany, isAdmin, user } = useAuth();
  const [tab, setTab] = useState("firma");
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [persons, setPersons] = useState([]);
  const [salarySubs, setSalarySubs] = useState([]);
  const [companyName, setCompanyName] = useState(company?.name || "");
  const [companyExtra, setCompanyExtra] = useState({
    legal_name: "", tax_id: "", vat_id: "", address: "", phone: "",
    email: "", website: "", iban: "", bank: "", report_header: "",
    notification_email: "", cantons: [],
  });
  const [cantonsList, setCantonsList] = useState([]);

  const load = async () => {
    const [u, m, p, c, ss, ct] = await Promise.all([
      api.get("/users"), api.get("/modules"), api.get("/persons"), api.get("/company"),
      api.get("/salary-subcategories").catch(() => ({ data: [] })),
      api.get("/cantons").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setModules(m.data); setPersons(p.data);
    setSalarySubs(ss.data || []);
    setCantonsList(ct.data || []);
    if (c.data) {
      setCompanyExtra({
        legal_name: c.data.legal_name || "", tax_id: c.data.tax_id || "",
        vat_id: c.data.vat_id || "", address: c.data.address || "",
        phone: c.data.phone || "", email: c.data.email || "",
        website: c.data.website || "", iban: c.data.iban || "",
        bank: c.data.bank || "", report_header: c.data.report_header || "",
        notification_email: c.data.notification_email || "",
        cantons: c.data.cantons || [],
      });
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setCompanyName(company?.name || ""); }, [company]);

  if (!isAdmin) return <div className="text-zinc-500">Acces restricționat.</div>;

  const saveLogo = async (f) => {
    await api.put("/company", { logo_file_id: f.id });
    await refreshCompany();
    toast.success("Logo actualizat");
  };
  const saveCompany = async () => {
    await api.put("/company", { name: companyName, ...companyExtra });
    await refreshCompany();
    toast.success("Salvat");
  };

  if (isDoaga(user)) return <div className="text-sm text-zinc-500 p-6" data-testid="restricted-msg">Nu ai acces la această secțiune.</div>;
  return (
    <div data-testid="page-settings">
      <h1 className="font-display text-3xl font-extrabold mb-1">Setări</h1>
      <p className="text-sm text-zinc-500 mb-6">Configurare firmă, utilizatori și module</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="firma" data-testid="tab-firma">Firmă & Logo</TabsTrigger>
          <TabsTrigger value="utilizatori" data-testid="tab-utilizatori">Utilizatori & Drepturi</TabsTrigger>
          <TabsTrigger value="module" data-testid="tab-module">Module</TabsTrigger>
          <TabsTrigger value="firme" data-testid="tab-firme">Firme angajatori</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="mentenanta" data-testid="tab-mentenanta">Mentenanță</TabsTrigger>
        </TabsList>

        <TabsContent value="firma">
          <div className="card-flat p-6 flex items-start gap-6 flex-wrap">
            <div className="flex flex-col items-center gap-3">
              {company?.logo_file_id ? (
                <AuthImg fileId={company.logo_file_id} className="w-32 h-32 rounded-md object-cover bg-zinc-50 border border-[var(--border)]" />
              ) : (
                <div className="w-32 h-32 rounded-md bg-zinc-100 grid place-items-center text-zinc-400">Fără logo</div>
              )}
              <UploadButton testId="upload-company-logo" label="Încarcă logo" onUploaded={saveLogo} />
            </div>
            <div className="flex-1 min-w-[300px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="small-label">Denumire firmă (afișată)</Label><Input className="mt-1" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="company-name" /></div>
                <div><Label className="small-label">Denumire legală</Label><Input className="mt-1" value={companyExtra.legal_name} onChange={(e) => setCompanyExtra({ ...companyExtra, legal_name: e.target.value })} placeholder="ex: ConVic SRL / GmbH" /></div>
                <div><Label className="small-label">Cod fiscal (CUI / CHE / UID)</Label><Input className="mt-1" value={companyExtra.tax_id} onChange={(e) => setCompanyExtra({ ...companyExtra, tax_id: e.target.value })} data-testid="company-tax-id"/></div>
                <div><Label className="small-label">TVA / MwSt</Label><Input className="mt-1" value={companyExtra.vat_id} onChange={(e) => setCompanyExtra({ ...companyExtra, vat_id: e.target.value })} /></div>
                <div className="md:col-span-2"><Label className="small-label">Adresă sediu</Label><Input className="mt-1" value={companyExtra.address} onChange={(e) => setCompanyExtra({ ...companyExtra, address: e.target.value })} /></div>
                <div><Label className="small-label">Telefon</Label><Input className="mt-1" value={companyExtra.phone} onChange={(e) => setCompanyExtra({ ...companyExtra, phone: e.target.value })} /></div>
                <div><Label className="small-label">Email</Label><Input className="mt-1" value={companyExtra.email} onChange={(e) => setCompanyExtra({ ...companyExtra, email: e.target.value })} /></div>
                <div><Label className="small-label">Website</Label><Input className="mt-1" value={companyExtra.website} onChange={(e) => setCompanyExtra({ ...companyExtra, website: e.target.value })} placeholder="https://..." /></div>
                <div><Label className="small-label">Bancă</Label><Input className="mt-1" value={companyExtra.bank} onChange={(e) => setCompanyExtra({ ...companyExtra, bank: e.target.value })} /></div>
                <div className="md:col-span-2"><Label className="small-label">IBAN</Label><Input className="mt-1 font-mono" value={companyExtra.iban} onChange={(e) => setCompanyExtra({ ...companyExtra, iban: e.target.value })} placeholder="CH00 0000 0000 0000 0000 0" /></div>
                <div className="md:col-span-2"><Label className="small-label">Antet rapoarte / observații</Label><Input className="mt-1" value={companyExtra.report_header} onChange={(e) => setCompanyExtra({ ...companyExtra, report_header: e.target.value })} placeholder="text afișat în partea de sus a rapoartelor exportate" /></div>
                <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
                  <Label className="small-label flex items-center gap-2">📧 Email notificări automate</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      className="font-mono"
                      value={companyExtra.notification_email}
                      onChange={(e) => setCompanyExtra({ ...companyExtra, notification_email: e.target.value })}
                      placeholder="info@convicgmbh.ch"
                      data-testid="company-notification-email"
                    />
                    <Button
                      variant="outline"
                      data-testid="send-test-email-btn"
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/email/test");
                          toast.success(`Email test trimis la ${data.to}`);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la trimitere"); }
                      }}
                    >Trimite test</Button>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">Aici primești digest-ul săptămânal și alertele critice. Lasă gol pentru a folosi adresa implicită <code>info@convicgmbh.ch</code>.</p>
                </div>

                <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
                  <Label className="small-label flex items-center gap-2">🇨🇭 Cantoane CH (sărbători cantonale)</Label>
                  <p className="text-[11px] text-zinc-500 mt-1 mb-3">Bifează cantoanele unde lucrezi — sărbătorile lor specifice vor fi marcate în calendarul Management.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="cantons-grid">
                    {cantonsList.map((c) => {
                      const checked = (companyExtra.cantons || []).includes(c.code);
                      return (
                        <label
                          key={c.code}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-xs transition-colors ${
                            checked ? "bg-[var(--primary-subtle)] border-[var(--primary)] text-[var(--primary)]" : "bg-white border-zinc-200 hover:border-zinc-300"
                          }`}
                          data-testid={`canton-${c.code}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(ev) => {
                              const cur = new Set(companyExtra.cantons || []);
                              if (ev.target.checked) cur.add(c.code); else cur.delete(c.code);
                              setCompanyExtra({ ...companyExtra, cantons: [...cur].sort() });
                            }}
                            className="accent-[var(--primary)]"
                          />
                          <span className="font-bold">{c.code}</span>
                          <span className="truncate text-[10px] text-zinc-500">{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Button onClick={saveCompany} className="mt-4 bg-[var(--primary)]" data-testid="save-company-btn">Salvează</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="utilizatori">
          <ErrorBoundary><UsersAdmin users={users} modules={modules} persons={persons} salarySubs={salarySubs} onChanged={load} /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="module">
          <ErrorBoundary><ModulesAdmin modules={modules} onChanged={load} /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="firme">
          <ErrorBoundary><EmployerCompaniesAdmin /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="audit">
          <ErrorBoundary><AuditLog /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="mentenanta">
          <ErrorBoundary><MaintenanceTools /></ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlanificatorDayWidthCard() {
  const [current, setCurrent] = useState(() => {
    try {
      return localStorage.getItem("planificator.day_col_width") || "auto";
    } catch {
      return "auto";
    }
  });
  const presets = [
    { value: "auto", label: "Automat (responsive — coloane egale)" },
    { value: "compact", label: "Compact · 180 px / coloană" },
    { value: "normal", label: "Normal · 280 px / coloană" },
    { value: "wide", label: "Larg · 360 px / coloană" },
    { value: "xwide", label: "Foarte larg · 440 px / coloană" },
  ];
  const apply = (val) => {
    setCurrent(val);
    try { localStorage.setItem("planificator.day_col_width", val); } catch { /* ignore */ }
    toast.success("Setare salvată — reîncarcă pagina Planificator pentru a vedea efectul", { duration: 4000 });
  };
  return (
    <div className="card-flat p-6" data-testid="planificator-day-width-card">
      <h2 className="font-display text-lg font-bold mb-1">Aspect Planificator</h2>
      <p className="text-sm text-zinc-600 mb-4">
        Controlează lățimea minimă a coloanelor de zi din Planificator. Util când vrei mai mult spațiu pe ecrane mari
        sau când lucrezi pe o tabletă/telefon. Setarea e salvată în acest browser și se aplică doar pentru tine.
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 max-w-xl">
        <Label className="sm:w-44 text-sm font-semibold">Lățime coloană:</Label>
        <Select value={current} onValueChange={apply}>
          <SelectTrigger className="w-full" data-testid="planificator-day-width-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-zinc-500 mt-3">
        Pentru lățimi peste „Automat", planificatorul va activa scroll orizontal pe ecranele mai înguste.
      </p>
    </div>
  );
}

function BackfillCalendarColorsButton() {
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const confirm = useConfirm();
  const run = async () => {
    const ok = await confirm({
      title: "Colorează retroactiv orele din calendar?",
      body: "Va parcurge toate intrările cu ore raportate fără project_id/color și va completa cu informațiile alocării corespunzătoare din planificator. Operația e sigură (NU șterge date).",
      confirmText: "DA, colorează",
      cancelText: "Anulează",
    });
    if (!ok) return;
    setRunning(true);
    try {
      const res = await api.post("/admin/backfill-calendar-colors");
      setLast(res.data);
      if ((res.data.updated || 0) === 0) {
        toast.success("Nimic de actualizat — toate intrările au deja culoare/proiect");
      } else {
        toast.success(`Colorate ${res.data.updated} intrări (${res.data.no_schedule_match} fără potrivire)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la backfill");
    } finally {
      setRunning(false);
    }
  };
  return (
    <div>
      <Button
        onClick={run}
        disabled={running}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        data-testid="backfill-calendar-colors-btn"
      >
        {running ? "Se procesează…" : "Colorează retroactiv calendar"}
      </Button>
      {last && (
        <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm">
          <div className="font-bold text-blue-900">Rezultat ultima rulare:</div>
          <div className="text-blue-800 mt-1">
            · Candidați procesați: <strong>{last.candidates}</strong><br/>
            · Intrări actualizate: <strong>{last.updated}</strong><br/>
            · Fără potrivire în planificator: <strong>{last.no_schedule_match}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

function MaintenanceTools() {
  const confirm = useConfirm();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const cleanup = async () => {
    const ok = await confirm({
      title: "Curăță alocările orfane?",
      body: "Vor fi șterse toate intrările din planificator (schedule_entries) care fac referire la proiecte/regii deja șterse, precum și override-urile de săptămână rămase orfane.\n\nEști sigur că vrei să ștergi?",
      confirmText: "DA, curăță acum",
      cancelText: "Anulează",
      danger: true,
    });
    if (!ok) return;
    setRunning(true);
    try {
      const res = await api.post("/admin/cleanup-orphan-entries");
      setLastResult(res.data);
      const total = (res.data.deleted_entries || 0) + (res.data.deleted_week_overrides || 0);
      if (total === 0) {
        toast.success("Curățenie completă · nimic de șters");
      } else {
        toast.success(`Curățate: ${res.data.deleted_entries} alocări orfane + ${res.data.deleted_week_overrides} override-uri`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la curățare");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4">
      <PlanificatorDayWidthCard />

      <div className="card-flat p-6">
        <h2 className="font-display text-lg font-bold mb-1">Curățare alocări orfane</h2>
        <p className="text-sm text-zinc-600 mb-4">
          Șterge intrările din planificator care fac referire la proiecte sau regii deja șterse. Util după ștergeri vechi care nu au curățat tot (înainte de cascade delete). Funcția este non-distructivă pentru proiectele/regiile active.
        </p>
        <Button
          onClick={cleanup}
          disabled={running}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="cleanup-orphan-entries-btn"
        >
          <Trash2 size={16} className="mr-2"/>
          {running ? "Se curăță…" : "Șterge alocările orfane"}
        </Button>
        {lastResult && (
          <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
            <div className="font-bold text-emerald-900">Rezultat ultima curățare:</div>
            <div className="text-emerald-800 mt-1">
              · Alocări orfane șterse: <strong>{lastResult.deleted_entries}</strong><br/>
              · Override-uri săptămână șterse: <strong>{lastResult.deleted_week_overrides}</strong>
            </div>
            {lastResult.samples?.length > 0 && (
              <div className="mt-2 text-xs text-emerald-700">
                Exemple: {lastResult.samples.map((s) => s.label).join(", ")}
                {lastResult.deleted_entries > lastResult.samples.length && ` …și încă ${lastResult.deleted_entries - lastResult.samples.length}`}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card-flat p-6">
        <h2 className="font-display text-lg font-bold mb-1">Colorare retroactivă calendar</h2>
        <p className="text-sm text-zinc-600 mb-4">
          Pentru orele istorice raportate fără proiect/culoare în calendar, caută în planificator
          alocarea persoanei pe acea zi și completează retroactiv <strong>color</strong>, <strong>project_id</strong> și <strong>project_label</strong>.
          Util pentru istoricul vechi când orele se introduceau direct în calendar fără referință de proiect.
        </p>
        <BackfillCalendarColorsButton />
      </div>

      <ReconcileAssignmentsCard />
      <TruncatePlanificatorGhostsCard />
    </div>
  );
}

function TruncatePlanificatorGhostsCard() {
  const confirm = useConfirm();
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const run = async () => {
    const ok = await confirm({
      title: "Curăță alocări fantomă din Planificator?",
      body: 'Această operațiune scanează TOATE alocările (schedule_entries) și:\n\n• Pentru alocările legate de o regie/proiect cu end_date setat → trunchiază end_date-ul alocării la cel al regiei/proiectului.\n• Pentru alocările care încep DUPĂ end_date-ul regiei/proiectului → le șterge complet.\n\nUtil pentru a elimina cazuri în care un angajat apare pe o regie/proiect care nu mai are activitate. Operația e sigură.',
      confirmText: "DA, curăță acum",
      cancelText: "Anulează",
    });
    if (!ok) return;
    setRunning(true);
    try {
      const res = await api.post("/admin/truncate-entries-past-end-date");
      setLast(res.data);
      const total = (res.data.truncated || 0) + (res.data.deleted || 0);
      if (total === 0) {
        toast.success("Planificator curat · nimic de trunchiat");
      } else {
        toast.success(`Curățate: ${res.data.truncated} alocări trunchiate + ${res.data.deleted} șterse`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la curățare");
    } finally { setRunning(false); }
  };
  return (
    <div className="card-flat p-6" data-testid="truncate-planificator-card">
      <h2 className="font-display text-lg font-bold mb-1">🧹 Curăță alocări fantomă (Planificator)</h2>
      <p className="text-sm text-zinc-600 mb-4">
        Folosit când o persoană apare alocată într-o regie/proiect care nu mai este activă (ex.: regie închisă sau cu end_date scurtat, dar entry-urile vechi din planificator au rămas extinse). Acest tool aliniază <code className="text-xs bg-zinc-100 px-1 rounded">schedule_entries.end_date</code> cu <code className="text-xs bg-zinc-100 px-1 rounded">regii.end_date</code> / <code className="text-xs bg-zinc-100 px-1 rounded">projects.end_date</code>.
      </p>
      <Button
        onClick={run}
        disabled={running}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="truncate-planificator-btn"
      >
        🧹 {running ? "Se curăță…" : "Curăță alocările fantomă"}
      </Button>
      {last && (
        <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
          <div className="font-bold text-emerald-900">Rezultat ultima curățare:</div>
          <div className="text-emerald-800 mt-1">
            · Alocări trunchiate (end_date ajustat): <strong>{last.truncated}</strong><br/>
            · Alocări șterse (începeau după sfârșit): <strong>{last.deleted}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

function ReconcileAssignmentsCard() {
  const confirm = useConfirm();
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const run = async () => {
    const ok = await confirm({
      title: "Repară alocări fantomă în inventar?",
      body: 'Această operațiune scanează TOATE item-urile din inventar (PSA + scule + materiale) și:\n\n• Sincronizează câmpurile legacy "assigned_to_person_id" cu cele moderne "assignee_person_id".\n• Pentru item-urile încă alocate persoanelor concediate/inactive → curăță alocarea și mută PSA la „SERGHEI ACASA" dacă nu are altă locație.\n\nOperația e sigură (NU șterge item-uri din inventar, doar curăță alocările fantomă).',
      confirmText: "DA, repară acum",
      cancelText: "Anulează",
    });
    if (!ok) return;
    setRunning(true);
    try {
      const res = await api.post("/inventory/admin/reconcile-assignments");
      setLast(res.data);
      const total = (res.data.synced_fields || 0) + (res.data.cleared_for_dismissed_persons || 0);
      if (total === 0) {
        toast.success("Inventar curat · nimic de reparat");
      } else {
        toast.success(`Reparate: ${res.data.synced_fields} câmpuri sincronizate + ${res.data.cleared_for_dismissed_persons} alocări fantomă curățate`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la reconciliere");
    } finally { setRunning(false); }
  };
  return (
    <div className="card-flat p-6" data-testid="reconcile-assignments-card">
      <h2 className="font-display text-lg font-bold mb-1">🔧 Repară alocări fantomă (Inventar)</h2>
      <p className="text-sm text-zinc-600 mb-4">
        Folosit atunci când un PSA preluat la concediere apare în continuare pe profilul angajatului concediat.
        Cauza: schema de bază de date păstra două câmpuri legacy (<code className="text-xs bg-zinc-100 px-1 rounded">assigned_to_person_id</code> și <code className="text-xs bg-zinc-100 px-1 rounded">assignee_person_id</code>), iar unele operațiuni vechi le actualizau dezincronizat. Acest tool le aliniază și curăță alocările rămase la persoane inactive.
      </p>
      <Button
        onClick={run}
        disabled={running}
        className="bg-amber-600 hover:bg-amber-700 text-white"
        data-testid="reconcile-assignments-btn"
      >
        🔧 {running ? "Se repară…" : "Repară alocările din inventar"}
      </Button>
      {last && (
        <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
          <div className="font-bold text-emerald-900">Rezultat ultima reparație:</div>
          <div className="text-emerald-800 mt-1">
            · Câmpuri sincronizate: <strong>{last.synced_fields}</strong><br/>
            · Alocări fantomă curățate (persoane inactive): <strong>{last.cleared_for_dismissed_persons}</strong><br/>
            · Total item-uri inventariate: <strong>{last.total_inventory_items}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

const ACTION_LABELS_MAP = {
  "user.create": { label: "Creare utilizator", color: "var(--green-valid)" },
  "user.update": { label: "Modificare utilizator", color: "var(--primary)" },
  "user.delete": { label: "Ștergere utilizator", color: "var(--red-bila)" },
  "user.unlock": { label: "Deblocare utilizator", color: "var(--orange-bila)" },
  "person.delete": { label: "Ștergere persoană", color: "var(--red-bila)" },
  "car.delete": { label: "Ștergere autoturism", color: "var(--red-bila)" },
  "module.delete": { label: "Ștergere modul", color: "var(--red-bila)" },
};

function AuditLog() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ action: "all", target_type: "all", username: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.action !== "all") params.set("action", filter.action);
      if (filter.target_type !== "all") params.set("target_type", filter.target_type);
      if (filter.username) params.set("username", filter.username.trim());
      params.set("limit", "200");
      const { data } = await api.get(`/audit-log?${params.toString()}`);
      setRows(data || []);
    } catch (e) { toast.error("Eroare încărcare audit log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.action, filter.target_type]);

  const fmtTs = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      const SS = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yy} ${HH}:${MM}:${SS}`;
    } catch { return s; }
  };

  const fmtDetails = (r) => {
    const d = r.details || {};
    // Helper defensiv — asigură că retur-ul este ÎNTOTDEAUNA string (nu obiect).
    const toStr = (v) => {
      if (v === null || v === undefined) return "—";
      if (typeof v === "string") return v;
      if (typeof v === "number" || typeof v === "boolean") return String(v);
      try { return JSON.stringify(v); } catch { return "[obiect]"; }
    };
    if (!d || typeof d !== "object" || Object.keys(d).length === 0) return toStr(d) || "—";
    // Audit detaliat cu diff old/new — afișăm prietenește
    if (d.diff && typeof d.diff === "object" && Object.keys(d.diff).length > 0) {
      const target = toStr(d.target_username || d.target_name || "").replace(/^—$/, "");
      const parts = Object.entries(d.diff).map(([field, v]) => {
        const oStr = toStr(v?.old);
        const nStr = toStr(v?.new);
        const o = oStr.length > 30 ? oStr.slice(0, 30) + "…" : oStr;
        const n = nStr.length > 30 ? nStr.slice(0, 30) + "…" : nStr;
        return `${field}: ${o} → ${n}`;
      }).join(" · ");
      const pwd = d.password_changed ? " · 🔐 parolă schimbată" : "";
      return `${target ? "@" + target + " · " : ""}${parts}${pwd}`;
    }
    if (typeof d.username === "string") return d.username;
    if (typeof d.label === "string") return d.label;
    if (typeof d.name === "string") return d.name;
    if (Array.isArray(d.changed)) {
      const items = d.changed.map(toStr).join(", ");
      return `câmpuri: ${items}${d.password_changed ? " (parolă)" : ""}`;
    }
    return Object.entries(d).map(([k, v]) => `${k}=${toStr(v)}`).join(", ");
  };

  return (
    <div className="space-y-4" data-testid="audit-log-view">
      <div className="card-flat p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[150px]">
          <Label className="small-label">Acțiune</Label>
          <Select value={filter.action} onValueChange={(v) => setFilter((f) => ({ ...f, action: v }))}>
            <SelectTrigger className="mt-1" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate acțiunile</SelectItem>
              {Object.entries(ACTION_LABELS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <Label className="small-label">Tip țintă</Label>
          <Select value={filter.target_type} onValueChange={(v) => setFilter((f) => ({ ...f, target_type: v }))}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate tipurile</SelectItem>
              <SelectItem value="user">user</SelectItem>
              <SelectItem value="person">person</SelectItem>
              <SelectItem value="car">car</SelectItem>
              <SelectItem value="module">module</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <Label className="small-label">Username actor</Label>
          <Input value={filter.username} onChange={(e) => setFilter((f) => ({ ...f, username: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="ex: admin" className="mt-1" data-testid="audit-filter-username" />
        </div>
        <Button onClick={load} variant="outline" data-testid="audit-refresh">Actualizează</Button>
        <Button
          variant="outline"
          data-testid="export-audit-csv"
          onClick={() => exportCSV(rows, [
            { key: (r) => {
              try {
                const d = new Date(r.ts);
                if (isNaN(d.getTime())) return r.ts;
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const HH = String(d.getHours()).padStart(2, "0");
                const MM = String(d.getMinutes()).padStart(2, "0");
                return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
              } catch { return r.ts; }
            }, label: "Dată/Oră" },
            { key: "username", label: "Username" },
            { key: "user_name", label: "Nume actor" },
            { key: (r) => (ACTION_LABELS_MAP[r.action]?.label || r.action), label: "Acțiune" },
            { key: "target_type", label: "Tip țintă" },
            { key: "target_id", label: "ID țintă" },
            { key: (r) => fmtDetails(r), label: "Detalii" },
          ], "audit_log")}
        >
          <Download size={16} className="mr-2"/> Export CSV
        </Button>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="p-3">Dată / Oră</th>
              <th className="p-3">Actor</th>
              <th className="p-3">Acțiune</th>
              <th className="p-3">Tip țintă</th>
              <th className="p-3">Detalii</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const safeStr = (v) => (v === null || v === undefined ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v)));
              const actionStr = safeStr(r.action);
              const meta = ACTION_LABELS_MAP[actionStr] || { label: actionStr || "?", color: "var(--zinc-500)" };
              return (
                <tr key={r.id} className="border-t border-[var(--border)] row-hover">
                  <td className="p-3 text-xs font-mono text-zinc-500 whitespace-nowrap">{fmtTs(r.ts)}</td>
                  <td className="p-3">
                    <div className="text-sm font-medium">{safeStr(r.user_name) || safeStr(r.username) || "—"}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">@{safeStr(r.username)}</div>
                  </td>
                  <td className="p-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${meta.color}1A`, color: meta.color }}>{safeStr(meta.label)}</span>
                  </td>
                  <td className="p-3 text-xs font-mono text-zinc-600">{safeStr(r.target_type) || "—"}</td>
                  <td className="p-3 text-xs text-zinc-700">{fmtDetails(r)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && <tr><td colSpan={5} className="p-8 text-center text-zinc-400 italic">Niciun eveniment găsit.</td></tr>}
            {loading && <tr><td colSpan={5} className="p-8 text-center text-zinc-400">Se încarcă…</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-zinc-400 text-right">Maxim 200 evenimente · păstrate 90 de zile.</div>
    </div>
  );
}

function UsersAdmin({ users, modules, persons, salarySubs = [], onChanged }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "user", person_id: "" });
  const [editing, setEditing] = useState(null);
  const [historyUser, setHistoryUser] = useState(null); // user pentru care afișăm dialog cu audit-history

  const create = async () => {
    try {
      await api.post("/users", { ...form, person_id: form.person_id || null });
      toast.success("Utilizator creat");
      setOpen(false);
      setForm({ username: "", password: "", name: "", role: "user", person_id: "" });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const setPerm = async (user, code, action, val) => {
    const perms = { ...(user.permissions || {}) };
    perms[code] = { ...(perms[code] || {}), [action]: val };
    await api.put(`/users/${user.id}`, { permissions: perms });
    onChanged();
  };

  const setSalarySubPerm = async (user, subCode, val) => {
    const perms = { ...(user.salary_subcategory_perms || {}) };
    if (val) perms[subCode] = true;
    else delete perms[subCode];
    await api.put(`/users/${user.id}`, { salary_subcategory_perms: perms });
    onChanged();
  };

  const setPersonalTabPerm = async (user, tabKey, val) => {
    const perms = { ...(user.personal_tab_perms || {}) };
    if (val) perms[tabKey] = true;
    else delete perms[tabKey];
    await api.put(`/users/${user.id}`, { personal_tab_perms: perms });
    onChanged();
  };

  const saveUser = async () => {
    await api.put(`/users/${editing.id}`, {
      name: editing.name, role: editing.role, person_id: editing.person_id || null,
      password: editing._newPassword || undefined,
      pickup_location: editing.pickup_location !== undefined ? editing.pickup_location : undefined,
    });
    toast.success("Salvat");
    setEditing(null);
    onChanged();
  };

  const del = async (u) => {
    if (!(await confirm({ body: `Șterge ${u.username}?`, danger: true }))) return;
    await api.delete(`/users/${u.id}`);
    onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="add-user-btn" onClick={() => setOpen(true)} className="bg-[var(--primary)]"><Plus size={16}/> Adaugă utilizator</Button>
      </div>

      {open && (
        <div className="card-flat p-6">
          <h3 className="font-display font-bold mb-4">Utilizator nou</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Username</Label><Input data-testid="new-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
            <div><Label>Parolă</Label><PasswordInput data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /><PasswordStrength value={form.password}/></div>
            <div><Label>Nume afișat</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (toate drepturile)</SelectItem>
                  <SelectItem value="planificator">Planificator</SelectItem>
                  <SelectItem value="user">Angajat</SelectItem>
                  <SelectItem value="magazioner">Magazioner / Mecanic</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-500 mt-1">Permisiunile sunt preconfigurate per rol; le poți rafina după creare.</p>
            </div>
            <div className="col-span-2">
              <Label>Persoană asociată (opțional - va vedea doar acest profil)</Label>
              <Select value={form.person_id || "none"} onValueChange={(v) => setForm({ ...form, person_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Nicio persoană" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nicio persoană —</SelectItem>
                  {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button data-testid="submit-new-user" onClick={create} className="bg-[var(--primary)]">Creează</Button>
          </div>
        </div>
      )}

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="p-3">Username</th>
              <th className="p-3">Nume</th>
              <th className="p-3">Rol</th>
              <th className="p-3">Persoană</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <React.Fragment key={u.id}>
                <tr className="border-t border-[var(--border)] row-hover">
                  <td className="p-3 font-mono">{u.username}</td>
                  <td className="p-3">{u.name || "—"}</td>
                  <td className="p-3">
                    {(() => {
                      const ROLE_BADGES = {
                        admin: { label: "Admin", cls: "bg-[var(--primary-subtle)] text-[var(--primary)]" },
                        planificator: { label: "Planificator", cls: "bg-blue-100 text-blue-700" },
                        magazioner: { label: "Magazioner", cls: "bg-purple-100 text-purple-700" },
                        user: { label: "Angajat", cls: "bg-zinc-100 text-zinc-600" },
                      };
                      const v = ROLE_BADGES[u.role] || ROLE_BADGES.user;
                      return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${v.cls}`}>{v.label}</span>;
                    })()}
                  </td>
                  <td className="p-3 text-xs text-zinc-500">{persons.find((p) => p.id === u.person_id) ? `${persons.find((p) => p.id === u.person_id).nume} ${persons.find((p) => p.id === u.person_id).prenume}` : "—"}</td>
                  <td className="p-3 text-right flex gap-2 justify-end">
                    {u.role !== "admin" && (
                      <button
                        className="text-zinc-600 text-sm hover:text-[var(--primary)] inline-flex items-center gap-1"
                        title="Deblochează contul (resetează încercările eșuate)"
                        data-testid={`unlock-${u.id}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/users/${u.id}/unlock`);
                            toast.success(`Cont deblocat (${data.cleared} încercări șterse)`);
                          } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
                        }}
                      >
                        <Unlock size={14}/>
                      </button>
                    )}
                    <button
                      className="text-zinc-600 text-sm hover:text-[var(--primary)] inline-flex items-center gap-1"
                      title="Istoric modificări cont (cine, când, de la ce la ce)"
                      data-testid={`audit-history-${u.id}`}
                      onClick={() => setHistoryUser(u)}
                    >
                      📜
                    </button>
                    <button className="text-[var(--primary)] text-sm hover:underline" onClick={() => setEditing({ ...u })}>Editează</button>
                    <button className="text-red-600 p-1 hover:bg-red-50 rounded" onClick={() => del(u)}><Trash2 size={14}/></button>
                  </td>
                </tr>
                {u.role !== "admin" && (
                  <tr className="border-t border-[var(--border)] bg-zinc-50/50">
                    <td colSpan={5} className="p-3">
                      <div className="text-xs font-semibold text-zinc-500 mb-2 uppercase tracking-wider">Drepturi pe module</div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {modules.map((m) => {
                          const perms = (u.permissions || {})[m.code] || {};
                          const isAvansuri = m.code === "AVANSURI";
                          const isPersonal = m.code === "PERSONAL";
                          const subPerms = u.salary_subcategory_perms || {};
                          const tabPerms = u.personal_tab_perms || {};
                          const PERSONAL_TABS = [
                            { key: "date", label: "Date" },
                            { key: "cazare", label: "Cazare" },
                            { key: "documente", label: "Documente" },
                            { key: "calendar", label: "Calendar Ore" },
                          ];
                          return (
                            <div key={m.code} className="border border-[var(--border)] rounded-md p-3 bg-white">
                              <div className="font-semibold text-sm mb-2">{m.name}</div>
                              <div className="flex flex-wrap gap-3">
                                {ACTIONS.map((a) => (
                                  <label key={a} className="flex items-center gap-2 text-xs">
                                    <Checkbox
                                      data-testid={`perm-${u.id}-${m.code}-${a}`}
                                      checked={!!perms[a]}
                                      onCheckedChange={(v) => setPerm(u, m.code, a, !!v)}
                                    />
                                    {ACTION_LABELS[a]}
                                  </label>
                                ))}
                              </div>
                              {isAvansuri && perms.view && (
                                <div className="mt-3 pt-2 border-t border-dashed border-zinc-200">
                                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold">Subcategorii vizibile</div>
                                  {salarySubs.length === 0 ? (
                                    <div className="text-[11px] text-zinc-400 italic">Nicio subcategorie definită.</div>
                                  ) : (
                                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                                      {salarySubs.map((s) => (
                                        <label key={s.code} className="flex items-center gap-1.5 text-xs">
                                          <Checkbox
                                            data-testid={`sub-perm-${u.id}-${s.code}`}
                                            checked={!!subPerms[s.code]}
                                            onCheckedChange={(v) => setSalarySubPerm(u, s.code, !!v)}
                                          />
                                          <span className="inline-flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }}/>
                                            {s.label}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {isPersonal && perms.view && (
                                <div className="mt-3 pt-2 border-t border-dashed border-zinc-200">
                                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold">Tab-uri vizibile</div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                                    {PERSONAL_TABS.map((t) => (
                                      <label key={t.key} className="flex items-center gap-1.5 text-xs">
                                        <Checkbox
                                          data-testid={`tab-perm-${u.id}-${t.key}`}
                                          checked={!!tabPerms[t.key]}
                                          onCheckedChange={(v) => setPersonalTabPerm(u, t.key, !!v)}
                                        />
                                        {t.label}
                                      </label>
                                    ))}
                                  </div>
                                  <div className="text-[10px] text-zinc-400 italic mt-1">Lasă goale toate pentru a permite toate tab-urile.</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/30 grid place-items-center z-50 p-4">
          <div className="card-flat p-6 w-full max-w-md bg-white">
            <h3 className="font-display font-bold text-lg mb-4">Editează utilizator</h3>
            <div className="space-y-3">
              <div><Label>Nume afișat</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div>
                <Label>Rol</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="planificator">Planificator</SelectItem>
                    <SelectItem value="user">Angajat</SelectItem>
                    <SelectItem value="magazioner">Magazioner / Mecanic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Persoană asociată</Label>
                <Select value={editing.person_id || "none"} onValueChange={(v) => setEditing({ ...editing, person_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nicio persoană —</SelectItem>
                    {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Parolă nouă (opțional)</Label><PasswordInput value={editing._newPassword || ""} onChange={(e) => setEditing({ ...editing, _newPassword: e.target.value })} /><PasswordStrength value={editing._newPassword || ""}/></div>
              {(editing.role === "admin" || editing.role === "magazioner") && (
                <div>
                  <Label>📦 Locație preluare PSA</Label>
                  <Input
                    value={editing.pickup_location || ""}
                    onChange={(e) => setEditing({ ...editing, pickup_location: e.target.value })}
                    placeholder="ex: ACASA LA VICTOR"
                    data-testid="edit-user-pickup-location"
                  />
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    Când acest user apasă „Preia PSA", echipamentul merge automat la această locație. Lăsat gol → fallback „SERGHEI ACASA".
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
              <Button onClick={saveUser} className="bg-[var(--primary)]">Salvează</Button>
            </div>
          </div>
        </div>
      )}

      {historyUser && <UserAuditHistoryDialog user={historyUser} onClose={() => setHistoryUser(null)} />}
    </div>
  );
}

function UserAuditHistoryDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/${user.id}/audit-history?limit=200`);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error("Eroare la încărcarea istoricului");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const fmtTs = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yy} ${HH}:${MM}`;
    } catch { return s; }
  };

  const ACTION_ICONS = {
    "user.create": { icon: "✨", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    "user.update": { icon: "✏️", color: "bg-blue-100 text-blue-800 border-blue-300" },
    "user.delete": { icon: "🗑", color: "bg-red-100 text-red-800 border-red-300" },
    "user.unlock": { icon: "🔓", color: "bg-amber-100 text-amber-800 border-amber-300" },
  };

  const stringify = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "boolean") return v ? "✓ DA" : "✗ NU";
    if (typeof v === "object") {
      try { return JSON.stringify(v, null, 2); } catch { return String(v); }
    }
    return String(v);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="user-audit-dialog">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-display text-lg font-bold flex items-center gap-2">📜 Istoric modificări cont</h2>
            <div className="text-xs text-zinc-500 mt-0.5">
              @<strong>{user.username}</strong> · {user.name} · rol curent: <strong>{user.role}</strong>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-2xl leading-none" data-testid="close-audit-dialog">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-zinc-400">Se încarcă…</div>
          ) : !data || (data.history || []).length === 0 ? (
            <div className="text-center py-8 text-zinc-400 italic">
              Nicio modificare înregistrată pentru acest cont.
              <div className="text-[11px] mt-1">Notă: audit detaliat (old/new) a fost activat recent. Modificările anterioare apar fără diff.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {data.history.map((evt) => {
                const m = ACTION_ICONS[evt.action] || ACTION_ICONS["user.update"];
                const diff = evt.details?.diff || {};
                const hasDiff = Object.keys(diff).length > 0;
                return (
                  <div key={evt.id} className="border border-zinc-200 rounded-md p-3 bg-white" data-testid={`audit-event-${evt.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded border ${m.color}`}>
                          {m.icon} {evt.action.replace("user.", "")}
                        </span>
                        <span className="text-xs text-zinc-500">{fmtTs(evt.ts)}</span>
                      </div>
                      <div className="text-[11px] text-zinc-600">
                        de <strong>@{evt.username}</strong> ({evt.user_name})
                      </div>
                    </div>
                    {hasDiff ? (
                      <div className="space-y-1.5 mt-2">
                        {Object.entries(diff).map(([field, v]) => (
                          <div key={field} className="text-xs grid grid-cols-[120px_1fr] gap-2 items-start">
                            <div className="font-mono font-semibold text-zinc-700 uppercase">{field}</div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="bg-red-50 text-red-900 border border-red-200 px-1.5 py-0.5 rounded text-[11px] max-w-[280px] overflow-hidden break-words" title={stringify(v.old)}>
                                {stringify(v.old)}
                              </code>
                              <span className="text-zinc-400">→</span>
                              <code className="bg-emerald-50 text-emerald-900 border border-emerald-200 px-1.5 py-0.5 rounded text-[11px] max-w-[280px] overflow-hidden break-words" title={stringify(v.new)}>
                                {stringify(v.new)}
                              </code>
                            </div>
                          </div>
                        ))}
                        {evt.details?.password_changed && (
                          <div className="text-xs text-amber-700 font-semibold mt-1">🔐 Parola a fost schimbată</div>
                        )}
                      </div>
                    ) : evt.details?.changed?.length > 0 ? (
                      <div className="text-xs text-zinc-600 italic">
                        Câmpuri schimbate: <strong>{evt.details.changed.join(", ")}</strong>
                        {evt.details.password_changed ? " (+ parolă)" : ""}
                        <div className="text-[10px] text-zinc-400 mt-0.5">⚠️ Audit-ul nu conține valori old/new (eveniment vechi).</div>
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500">{JSON.stringify(evt.details || {})}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModulesAdmin({ modules, onChanged }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", icon: "layers" });

  const add = async () => {
    try {
      await api.post("/modules", form);
      toast.success("Modul adăugat");
      setOpen(false);
      setForm({ code: "", name: "", icon: "layers" });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const del = async (m) => {
    if (!(await confirm({ body: "Șterge modulul?", danger: true }))) return;
    try { await api.delete(`/modules/${m.id}`); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="bg-[var(--primary)]" data-testid="add-module-btn"><Plus size={16}/> Modul nou</Button>
      </div>
      {open && (
        <div className="card-flat p-6">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Cod (unic, majuscule)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div><Label>Denumire</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Icon</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="layers" /></div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={add} className="bg-[var(--primary)]">Creează</Button>
          </div>
        </div>
      )}
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="p-3">Cod</th>
              <th className="p-3">Denumire</th>
              <th className="p-3">Tip</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.code} className="border-t border-[var(--border)]">
                <td className="p-3 font-mono">{m.code}</td>
                <td className="p-3">{m.name}</td>
                <td className="p-3 text-xs text-zinc-500">{m.system ? "Sistem" : "Custom"}</td>
                <td className="p-3 text-right">
                  {!m.system && <button onClick={() => del(m)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function EmployerCompaniesAdmin() {
  const confirm = useConfirm();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/employer-companies");
      setRows(data || []);
    } catch { toast.error("Eroare la încărcare firme"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Numele firmei este obligatoriu"); return; }
    try {
      if (editingId) {
        await api.put(`/employer-companies/${editingId}`, { name: trimmed, note, active: true });
        toast.success("Firmă actualizată");
      } else {
        await api.post("/employer-companies", { name: trimmed, note, active: true });
        toast.success("Firmă adăugată");
      }
      setName(""); setNote(""); setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const startEdit = (r) => { setEditingId(r.id); setName(r.name || ""); setNote(r.note || ""); };
  const cancelEdit = () => { setEditingId(null); setName(""); setNote(""); };

  const del = async (r) => {
    if (!(await confirm({ body: `Ștergi firma "${r.name}"? Persoanele asociate vor rămâne fără firmă.`, danger: true }))) return;
    try {
      await api.delete(`/employer-companies/${r.id}`);
      toast.success("Firmă ștearsă");
      load();
    } catch { toast.error("Eroare la ștergere"); }
  };

  return (
    <div className="space-y-4" data-testid="employer-companies-admin">
      <div className="card-flat p-5">
        <h3 className="font-display text-lg font-bold mb-1">{editingId ? "Editare firmă" : "Adaugă firmă nouă"}</h3>
        <p className="text-xs text-zinc-500 mb-3">Listele acestea apar în drop-down-ul „Firma angajatoare" la fiecare persoană.</p>
        {!isAdmin && <p className="text-sm text-amber-600 mb-2">Doar administratorii pot adăuga/edita firme.</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="small-label">Nume firmă</Label><Input data-testid="employer-name-input" value={name} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} placeholder="ex: ConVic SRL" /></div>
          <div className="md:col-span-2"><Label className="small-label">Notă (opțional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} disabled={!isAdmin} placeholder="ex: subcontractor regulat" /></div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={submit} disabled={!isAdmin} className="bg-[var(--primary)]" data-testid="employer-save-btn">{editingId ? "Salvează modificări" : "Adaugă"}</Button>
          {editingId && <Button variant="outline" onClick={cancelEdit}>Anulează</Button>}
        </div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-[var(--border)]">
            <tr>
              <th className="p-3 text-left text-xs font-semibold text-zinc-500 uppercase">Nume</th>
              <th className="p-3 text-left text-xs font-semibold text-zinc-500 uppercase">Notă</th>
              <th className="p-3 text-right text-xs font-semibold text-zinc-500 uppercase">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={3} className="p-4 text-center text-sm text-zinc-500 italic">Nicio firmă adăugată încă.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`employer-row-${r.id}`}>
                <td className="p-3 text-sm font-medium">{r.name}</td>
                <td className="p-3 text-sm text-zinc-600">{r.note || "—"}</td>
                <td className="p-3 text-right">
                  {isAdmin && <>
                    <button onClick={() => startEdit(r)} className="text-[var(--primary)] p-1 hover:bg-[var(--primary-subtle)] rounded mr-1" title="Editează">Edit</button>
                    <button onClick={() => del(r)} className="text-red-600 p-1 hover:bg-red-50 rounded" title="Șterge"><Trash2 size={14}/></button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
